import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDashboardStats, fetchDashboardRecent } from '../store/slices/dashboardSlice';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedPeriod, setSelectedPeriod] = useState('7d');

  const {
    stats,
    recentActivities,
    recentInvoices,
    activePromotions,
    templateStatus,
    pendingTasks,
    topCustomers,
    statsLoading,
    recentLoading,
    statsError,
    recentError,
  } = useSelector((state) => state.dashboard);

  const loadDashboardData = useCallback(
    (period) => {
      dispatch(fetchDashboardStats({ period }));
      dispatch(fetchDashboardRecent({ period }));
    },
    [dispatch]
  );

  useEffect(() => {
    loadDashboardData(selectedPeriod);
  }, [selectedPeriod, loadDashboardData]);

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      'Paid': { color: '#10b981', bg: '#d1fae5' },
      'Pending': { color: '#f59e0b', bg: '#fef3c7' },
      'Overdue': { color: '#ef4444', bg: '#fee2e2' },
      'Draft': { color: '#6b7280', bg: '#f3f4f6' },
      'Sent': { color: '#667eea', bg: '#ede9fe' },
    };
    const s = status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : 'Pending';
    const statusConfig = config[s] || config['Pending'];
    return (
      <span className="status-badge" style={{ background: statusConfig.bg, color: statusConfig.color }}>
        {s}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      'high': { color: '#ef4444', bg: '#fee2e2' },
      'medium': { color: '#f59e0b', bg: '#fef3c7' },
      'low': { color: '#6b7280', bg: '#f3f4f6' }
    };
    const priorityConfig = config[priority] || config['low'];
    return (
      <span className="priority-badge" style={{ background: priorityConfig.bg, color: priorityConfig.color }}>
        {priority.toUpperCase()}
      </span>
    );
  };

  const isLoading = statsLoading || recentLoading;
  const hasError = statsError || recentError;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p>Welcome back! Here's what's happening with your business.</p>
        </div>
        <div className="period-selector">
          <button
            className={`period-btn ${selectedPeriod === '7d' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('7d')}
          >
            7 Days
          </button>
          <button
            className={`period-btn ${selectedPeriod === '30d' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('30d')}
          >
            30 Days
          </button>
          <button
            className={`period-btn ${selectedPeriod === '90d' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('90d')}
          >
            90 Days
          </button>
        </div>
      </div>

      {hasError && (
        <div
          style={{
            background: '#fee2e2',
            color: '#ef4444',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>⚠️ {statsError || recentError}</span>
          <button
            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 600 }}
            onClick={() => loadDashboardData(selectedPeriod)}
          >
            Retry
          </button>
        </div>
      )}

      <div className="stats-grid">
        {isLoading && stats.length === 0
          ? Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="stat-card" style={{ minHeight: 100, opacity: 0.4, background: '#f3f4f6' }} />
            ))
          : stats.map((stat, index) => (
          <div 
            key={index} 
            className="stat-card"
            onClick={() => stat.link && navigate(stat.link)}
            style={{ cursor: stat.link ? 'pointer' : 'default' }}
          >
            <div className="stat-icon" style={{ background: `${stat.color}20`, color: stat.color }}>
              {stat.icon}
            </div>
            <div className="stat-content">
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
              <div className={`stat-change ${stat.change?.startsWith('+') ? 'positive' : stat.change?.startsWith('-') ? 'negative' : 'neutral'}`}>
                {stat.change}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="dashboard-grid">
        {/* Recent Activity */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Recent Activity</h3>
            <button className="view-all-btn" onClick={() => navigate('/conversations')}>
              View All
            </button>
          </div>
          <div className="activity-list">
            {recentLoading && recentActivities.length === 0 ? (
              <div style={{ padding: '16px', color: '#6b7280', textAlign: 'center' }}>Loading...</div>
            ) : recentActivities.length === 0 ? (
              <div style={{ padding: '16px', color: '#6b7280', textAlign: 'center' }}>No recent activity</div>
            ) : (
            recentActivities.map((activity, index) => (
              <div 
                key={index} 
                className="activity-item"
                onClick={() => navigate(activity.link || '/conversations')}
                style={{ cursor: 'pointer' }}
              >
                <div className="activity-icon">
                  {activity.type === 'message' && '📩'}
                  {activity.type === 'start' && '🆕'}
                  {activity.type === 'sent' && '📤'}
                  {activity.type === 'reply' && '↩️'}
                  {activity.type === 'lead' && '📋'}
                  {activity.type === 'appointment' && '📅'}
                </div>
                <div className="activity-content">
                  <div className="activity-text">
                    <strong>{activity.customer}</strong> - {activity.action}
                  </div>
                  <div className="activity-time">{activity.time}</div>
                </div>
              </div>
            ))
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Quick Actions</h3>
          </div>
          <div className="quick-actions">
            <button 
              className="action-btn"
              onClick={() => navigate('/conversations')}
            >
              <span className="action-icon">➕</span>
              <span>New Conversation</span>
            </button>
            <button 
              className="action-btn"
              onClick={() => navigate('/templates')}
            >
              <span className="action-icon">📝</span>
              <span>Create Template</span>
            </button>
            <button 
              className="action-btn"
              onClick={() => navigate('/promotions')}
            >
              <span className="action-icon">🎯</span>
              <span>Create Promotion</span>
            </button>
            <button 
              className="action-btn"
              onClick={() => navigate('/invoices')}
            >
              <span className="action-icon">🧾</span>
              <span>Create Invoice</span>
            </button>
            <button 
              className="action-btn"
              onClick={() => navigate('/customers')}
            >
              <span className="action-icon">👥</span>
              <span>Add Customer</span>
            </button>
            <button 
              className="action-btn"
              onClick={() => navigate('/analytics')}
            >
              <span className="action-icon">📊</span>
              <span>View Analytics</span>
            </button>
          </div>
        </div>

        {/* Recent Invoices */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Recent Invoices</h3>
            <button className="view-all-btn" onClick={() => navigate('/invoices')}>
              View All
            </button>
          </div>
          <div className="invoices-list">
            {recentLoading && recentInvoices.length === 0 ? (
              <div style={{ padding: '16px', color: '#6b7280', textAlign: 'center' }}>Loading...</div>
            ) : recentInvoices.length === 0 ? (
              <div style={{ padding: '16px', color: '#6b7280', textAlign: 'center' }}>No invoices yet</div>
            ) : (
            recentInvoices.map((invoice) => (
              <div 
                key={invoice.id} 
                className="invoice-item"
                onClick={() => navigate('/invoices')}
                style={{ cursor: 'pointer' }}
              >
                <div className="invoice-info">
                  <div className="invoice-id">{invoice.invoiceNumber}</div>
                  <div className="invoice-customer">{invoice.customer}</div>
                </div>
                <div className="invoice-details">
                  <div className="invoice-amount">{formatCurrency(invoice.amount)}</div>
                  <div className="invoice-meta">
                    {getStatusBadge(invoice.status)}
                    <span className="invoice-date">{formatDate(invoice.date)}</span>
                  </div>
                </div>
              </div>
            ))
            )}
          </div>
        </div>

        {/* Active Promotions */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Active Promotions</h3>
            <button className="view-all-btn" onClick={() => navigate('/promotions')}>
              View All
            </button>
          </div>
          <div className="promotions-list">
            {recentLoading && activePromotions.length === 0 ? (
              <div style={{ padding: '16px', color: '#6b7280', textAlign: 'center' }}>Loading...</div>
            ) : activePromotions.length === 0 ? (
              <div style={{ padding: '16px', color: '#6b7280', textAlign: 'center' }}>No active promotions</div>
            ) : (
            activePromotions.map((promo) => {
              const ctr = promo.sent > 0 ? ((promo.clicked / promo.sent) * 100).toFixed(1) : '0.0';
              const conversion = promo.clicked > 0 ? ((promo.converted / promo.clicked) * 100).toFixed(1) : '0.0';
              return (
                <div 
                  key={promo.id} 
                  className="promotion-item"
                  onClick={() => navigate('/promotions')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="promotion-title">{promo.title}</div>
                  <div className="promotion-stats">
                    <div className="promo-stat">
                      <span className="stat-label">Sent:</span>
                      <span className="stat-value">{promo.sent.toLocaleString()}</span>
                    </div>
                    <div className="promo-stat">
                      <span className="stat-label">CTR:</span>
                      <span className="stat-value">{ctr}%</span>
                    </div>
                    <div className="promo-stat">
                      <span className="stat-label">Revenue:</span>
                      <span className="stat-value revenue">{formatCurrency(promo.revenue)}</span>
                    </div>
                  </div>
                </div>
              );
            })
            )}
          </div>
        </div>

        {/* Template Status */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Template Status</h3>
            <button className="view-all-btn" onClick={() => navigate('/templates')}>
              View All
            </button>
          </div>
          <div className="template-status-grid">
            <div className="status-item approved">
              <div className="status-value">{templateStatus.approved}</div>
              <div className="status-label">Approved</div>
            </div>
            <div className="status-item pending">
              <div className="status-value">{templateStatus.pending}</div>
              <div className="status-label">Pending</div>
            </div>
            <div className="status-item rejected">
              <div className="status-value">{templateStatus.rejected}</div>
              <div className="status-label">Rejected</div>
            </div>
            <div className="status-item total">
              <div className="status-value">{templateStatus.total}</div>
              <div className="status-label">Total</div>
            </div>
          </div>
        </div>

        {/* Pending Tasks & Alerts */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Pending Tasks & Alerts</h3>
          </div>
          <div className="tasks-list">
            {recentLoading && pendingTasks.length === 0 ? (
              <div style={{ padding: '16px', color: '#6b7280', textAlign: 'center' }}>Loading...</div>
            ) : pendingTasks.length === 0 ? (
              <div style={{ padding: '16px', color: '#10b981', textAlign: 'center' }}>
                ✅ All caught up! No pending tasks.
              </div>
            ) : (
            pendingTasks.map((task) => (
              <div 
                key={task.id} 
                className="task-item"
                onClick={() => navigate(task.link)}
                style={{ cursor: 'pointer' }}
              >
                <div className="task-icon">
                  {task.type === 'template' && '📝'}
                  {task.type === 'invoice' && '🧾'}
                  {task.type === 'appointment' && '📅'}
                  {task.type === 'lead' && '📋'}
                </div>
                <div className="task-content">
                  <div className="task-message">{task.message}</div>
                  {getPriorityBadge(task.priority)}
                </div>
              </div>
            ))
            )}
          </div>
        </div>

        {/* Upcoming Appointments */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Upcoming Appointments</h3>
            <button className="view-all-btn" onClick={() => navigate('/appointments')}>
              View All
            </button>
          </div>
          <div className="appointments-list">
            <div style={{ padding: '16px', color: '#6b7280', textAlign: 'center' }}>
              Manage appointments in the Appointments page.
            </div>
          </div>
        </div>

        {/* Top Customers */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Top Customers</h3>
            <button className="view-all-btn" onClick={() => navigate('/customers')}>
              View All
            </button>
          </div>
          <div className="customers-list">
            {recentLoading && topCustomers.length === 0 ? (
              <div style={{ padding: '16px', color: '#6b7280', textAlign: 'center' }}>Loading...</div>
            ) : topCustomers.length === 0 ? (
              <div style={{ padding: '16px', color: '#6b7280', textAlign: 'center' }}>No customers yet</div>
            ) : (
            topCustomers.map((customer, index) => (
              <div 
                key={customer.id} 
                className="customer-item"
                onClick={() => navigate(`/customers/${customer.id}/chat`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="customer-rank">#{index + 1}</div>
                <div className="customer-info">
                  <div className="customer-name">{customer.name}</div>
                  <div className="customer-stats">
                    <span>{customer.orders} messages</span>
                    <span className="customer-revenue">{formatCurrency(customer.revenue)}</span>
                  </div>
                </div>
                <span className="customer-status active">Active</span>
              </div>
            ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
